import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronRight } from "lucide-react";

import Header from "../components/Header";
import Footer from "../components/Footer";
import FloatingButtons from "../components/FloatingButtons";
import ProductCard from "../components/ProductCard";
import QuickViewDialog from "../components/QuickViewDialog";
import { useProducts } from "../lib/ProductsContext";
import { useCart } from "../lib/CartContext";
import { optimizeImg } from "../lib/img";

export default function Category() {
  const { categoryId } = useParams();
  const { categories, products } = useProducts();
  const { addToCart } = useCart();
  const [quickView, setQuickView] = useState(null);

  const category = useMemo(() => categories.find((c) => c.id === categoryId), [categoryId, categories]);
  const categoryProducts = useMemo(
    () => products.filter((p) => p.cat === categoryId),
    [categoryId, products]
  );

  if (!category) {
    return (
      <div className="min-h-screen bg-white text-[#222222]" data-testid="category-not-found">
        <Header />
        <div className="frida-container py-32 text-center">
          <h1 className="font-serif text-3xl mb-4">Kategori bulunamadı</h1>
          <Link to="/" className="text-[#A85C34] font-medium hover:underline">
            Ana sayfaya dön
          </Link>
        </div>
        <Footer />
        <FloatingButtons />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-[#222222]" data-testid="category-page">
      <Header />

      {/* Category hero */}
      <section className="relative h-[280px] md:h-[340px] overflow-hidden">
        <img
          src={optimizeImg(category.image, 1600)}
          alt={category.name}
          decoding="async"
          fetchPriority="high"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/10" />
        <div className="relative frida-container h-full flex flex-col justify-end pb-10 text-white">
          <nav className="flex items-center gap-2 text-xs text-white/75 mb-3" data-testid="category-breadcrumb">
            <Link to="/" className="hover:text-white">Ana Sayfa</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/kategoriler" className="hover:text-white">Kategoriler</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white">{category.name}</span>
          </nav>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight" data-testid="category-title">
            {category.name}
          </h1>
          <p className="mt-2 text-white/80">
            {categoryProducts.length} ürün listeleniyor
          </p>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-[#F8F8F8]">
        <div className="frida-container">
          {categoryProducts.length === 0 ? (
            <div className="text-center py-20" data-testid="category-empty">
              <p className="text-[#555555] text-lg">
                Bu kategoride henüz ürün bulunmuyor. Yakında eklenecek.
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 mt-6 px-7 py-3.5 bg-[#A85C34] hover:bg-[#8B4726] text-white rounded-full font-medium transition-colors"
              >
                Tüm Ürünlere Göz At
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 lg:gap-8" data-testid="category-products-grid">
              {categoryProducts.map((p) => (
                <ProductCard
                  key={p.id}
                  product={p}
                  onAddToCart={addToCart}
                  onQuickView={setQuickView}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
      <FloatingButtons />

      <QuickViewDialog
        product={quickView}
        onClose={() => setQuickView(null)}
        onAddToCart={addToCart}
      />
    </div>
  );
}
